/**
 * Settings Page Component
 * Coláiste Lurgan Management System - System settings and administration
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserPermissions } from '../../hooks/useAuth';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import UserManagement from '../settings/UserManagement';
import SchoolData from '../settings/SchoolData';
import FieldPermissionsTab from '../settings/FieldPermissionsTab';
import PagePermissionsTab from '../settings/PagePermissionsTab';
import SystemSettings from '../settings/SystemSettings';
import EmailTemplatesTab from '../settings/EmailTemplatesTab';
import OideSyncPanel from '../settings/OideSyncPanel';
import ClaruStatsPanel from '../settings/ClaruStatsPanel';
import {
  Settings as SettingsIcon,
  Shield,
  Users,
  Globe,
  Database,
  Mail,
  Link2
} from 'lucide-react';

export function Settings() {
  const { userType, hasPermission } = useUserPermissions();
  const { t } = useTranslation();

  // Check if user is admin
  const isAdmin = userType === 'ADMIN';
  const canManageUsers = isAdmin || hasPermission('manage_users');
  const canManageEmailTemplates = isAdmin || userType === 'OFFICE';

  // Set default tab based on permissions (permissions and system tabs hidden for go-live)
  const getDefaultTab = () => {
    if (canManageUsers) return 'users';
    if (isAdmin) return 'schoolData'; // Default to School Data for admin
    return 'users'; // fallback
  };

  const [activeTab, setActiveTab] = useState(getDefaultTab());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('settings.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('settings.description')}
          </p>
        </div>
        <Badge variant={isAdmin ? 'default' : 'secondary'}>
          {t(`userRole.${userType}`)}
        </Badge>
      </div>

      {/* Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-1 lg:grid-cols-7">
          {canManageUsers && (
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">{t('settings.tabs.users')}</span>
            </TabsTrigger>
          )}

          {/* Hidden for go-live - will be enabled later */}
          {/* {canManagePermissions && (
            <TabsTrigger value="permissions" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">{t('settings.tabs.permissions')}</span>
            </TabsTrigger>
          )} */}

          {isAdmin && (
            <TabsTrigger value="system" className="flex items-center gap-2">
              <SettingsIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{t('settings.tabs.system', 'System')}</span>
            </TabsTrigger>
          )}

          {isAdmin && (
            <TabsTrigger value="schoolData" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              <span className="hidden sm:inline">{t('settings.tabs.schoolData', 'School Data')}</span>
            </TabsTrigger>
          )}

          {isAdmin && (
            <TabsTrigger value="fieldPermissions" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">{t('settings.tabs.fieldPermissions', 'Field Permissions')}</span>
            </TabsTrigger>
          )}

          {isAdmin && (
            <TabsTrigger value="pagePermissions" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline">{t('settings.tabs.pagePermissions', 'Page Permissions')}</span>
            </TabsTrigger>
          )}

          {canManageEmailTemplates && (
            <TabsTrigger value="emailTemplates" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">{t('settings.tabs.emailTemplates', 'Email Templates')}</span>
            </TabsTrigger>
          )}

          {isAdmin && (
            <TabsTrigger value="integrations" className="flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              <span className="hidden sm:inline">{t('settings.tabs.integrations', 'Integrations')}</span>
            </TabsTrigger>
          )}
        </TabsList>


        {/* Users Management Tab - Admin Only */}
        {canManageUsers && (
          <TabsContent value="users" className="space-y-6">
            <UserManagement />
          </TabsContent>
        )}

        {/* Hidden for go-live: Permissions Management Tab and System Tab will be enabled later */}

        {/* System Settings Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="system" className="space-y-6">
            <SystemSettings />
          </TabsContent>
        )}

        {/* School Data Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="schoolData" className="space-y-6">
            <SchoolData />
          </TabsContent>
        )}

        {/* Field Permissions Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="fieldPermissions" className="space-y-6">
            <FieldPermissionsTab />
          </TabsContent>
        )}

        {/* Page Permissions Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="pagePermissions" className="space-y-6">
            <PagePermissionsTab />
          </TabsContent>
        )}

        {/* Email Templates Tab - Admin + Office */}
        {canManageEmailTemplates && (
          <TabsContent value="emailTemplates" className="space-y-6">
            <EmailTemplatesTab />
          </TabsContent>
        )}

        {/* Integrations Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="integrations" className="space-y-6">
            <OideSyncPanel />
            <ClaruStatsPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export default Settings;